import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import type {
	CandidateSettingsDTO,
	ChangeOwnPasswordDTO,
	CompanySettingsDTO,
	PortalAccountDTO,
	UpdateCandidateSettingsDTO,
	UpdateCompanySettingsDTO,
	UpdatePortalAccountDTO,
} from '@libs/dtos/merit/settings.dto';
import type { CreateVenueDTO, UpdateVenueDTO, VenueDTO } from '@libs/dtos/merit/venue.dto';
import type { Observable } from 'rxjs';

/** M13-lite portal settings for both roles. */
@Injectable({ providedIn: 'root' })
export class PortalSettingsService {
	private readonly http = inject(HttpClient);
	private readonly base = `${environment.pathBackend}/portal`;

	/**
	 * Change your own password. Not the admin route (`PUT /users/:id/password`):
	 * that one takes any id and makes the current password optional, which is
	 * correct for a root resetting access and wrong for self-service.
	 */
	changeOwnPassword(dto: ChangeOwnPasswordDTO): Observable<{ ok: boolean }> {
		return this.http.put<{ ok: boolean }>(`${this.base}/me/password`, dto);
	}

	/** "Mi cuenta": the person behind the session, in both portals. */
	getMyAccount(): Observable<PortalAccountDTO> {
		return this.http.get<PortalAccountDTO>(`${this.base}/me/account`);
	}

	updateMyAccount(dto: UpdatePortalAccountDTO): Observable<{ ok: boolean }> {
		return this.http.put<{ ok: boolean }>(`${this.base}/me/account`, dto);
	}

	getCandidateSettings(): Observable<CandidateSettingsDTO> {
		return this.http.get<CandidateSettingsDTO>(`${this.base}/candidate/settings`);
	}

	/** Language preference for a portal account (the staff route is staff-only). */
	updateMyLanguage(preferredLanguage: string): Observable<{ ok: boolean }> {
		return this.http.put<{ ok: boolean }>(`${this.base}/me/language`, { preferredLanguage });
	}

	updateCandidateSettings(dto: UpdateCandidateSettingsDTO): Observable<{ ok: boolean }> {
		return this.http.patch<{ ok: boolean }>(`${this.base}/candidate/settings`, dto);
	}

	getCompanySettings(): Observable<CompanySettingsDTO> {
		return this.http.get<CompanySettingsDTO>(`${this.base}/company/settings`);
	}

	// ---- Venues (MD-Planes §3) ---------------------------------------------
	// The company's own management door over the same API the admin's 360º
	// uses. Until 17/08 only the admin had a screen for this: the vacancy
	// wizard's selector read venues but nothing in the portal could create one.

	/** Inactive ones included: this is the management list, not a selector. */
	listCompanyVenues(): Observable<VenueDTO[]> {
		return this.http.get<VenueDTO[]>(`${this.base}/company/venues`, { params: { includeInactive: 'true' } });
	}

	createCompanyVenue(dto: CreateVenueDTO): Observable<VenueDTO> {
		return this.http.post<VenueDTO>(`${this.base}/company/venues`, dto);
	}

	updateCompanyVenue(id: number, dto: UpdateVenueDTO): Observable<VenueDTO> {
		return this.http.patch<VenueDTO>(`${this.base}/company/venues/${id}`, dto);
	}

	/** Deleted only when nothing points at it; deactivated otherwise. */
	removeCompanyVenue(id: number): Observable<VenueDTO | null> {
		return this.http.delete<VenueDTO | null>(`${this.base}/company/venues/${id}`);
	}

	/** Logo o imagen de la propiedad (08/09): se guarda al momento, fuera del «Guardar» del perfil. */
	uploadCompanyLogo(file: File): Observable<{ logoUrl: string | null }> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<{ logoUrl: string | null }>(`${this.base}/company/settings/logo`, formData);
	}

	removeCompanyLogo(): Observable<{ ok: boolean }> {
		return this.http.delete<{ ok: boolean }>(`${this.base}/company/settings/logo`);
	}

	updateCompanySettings(dto: UpdateCompanySettingsDTO): Observable<{ ok: boolean }> {
		return this.http.patch<{ ok: boolean }>(`${this.base}/company/settings`, dto);
	}

	/** PDPL · derecho de acceso: todo lo que la plataforma guarda del candidato. */
	exportCandidateData(): Observable<Record<string, unknown>> {
		return this.http.get<Record<string, unknown>>(`${this.base}/candidate/settings/export`);
	}

	/** PDPL · pide la supresion; la cuenta sigue viva hasta que el equipo la tramita. */
	requestCandidateDeletion(): Observable<{ ok: boolean }> {
		return this.http.post<{ ok: boolean }>(`${this.base}/candidate/settings/deletion-request`, {});
	}

	/** PDPL · supresion inmediata: corta el acceso ya y deja el borrado en cola. */
	deleteCandidateAccount(): Observable<{ ok: boolean }> {
		return this.http.delete<{ ok: boolean }>(`${this.base}/candidate/settings/account`);
	}

	/** Invite a colleague into the company account (M13). */
	inviteCompanyMember(dto: { name: string; lastName: string; email: string }): Observable<{ ok: boolean }> {
		return this.http.post<{ ok: boolean }>(`${this.base}/company/settings/team`, dto);
	}

	/** Quitar el acceso a un miembro (solo el propietario). */
	revokeCompanyMember(userId: number): Observable<{ ok: boolean }> {
		return this.http.delete<{ ok: boolean }>(`${this.base}/company/settings/team/${userId}`);
	}
}
